import { useContext } from "react";
import PropTypes from "prop-types";
import { ActionButton, TooltipTrigger, Tooltip } from "@react-spectrum/s2";
import UndoIcon from "@react-spectrum/s2/icons/Undo";
import RedoIcon from "@react-spectrum/s2/icons/Redo";

import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import { EditPrompt } from "../EditPrompt";
import { Step, stepFooterStyle, stepFooterPromptStyles } from "./Step";

/**
 * The campaign fine-tuning bar of a step: the `EditPrompt` that adjusts the
 * selected campaign, with undo/redo through its history. `editing` is the
 * step's `useCampaignEditing()`. Its completions are the campaign's
 * `promptHintsKey` hints, falling back to its general `promptHints`.
 */
export const CampaignPromptFooter = ({
  editing,
  placeholder,
  promptHintsKey = "promptHints",
}) => {
  const {
    video,
    selectedCampaign,
    updateVideo,
    customerBranding,
    handleRegenerateScript,
  } = useContext(VideoWizardContext);

  const {
    hasHistoricalCampaigns,
    hasPreviousCampaign,
    hasNextCampaign,
    handleSubmit,
    handleUndo,
    handleRedo,
  } = editing;

  return (
    <Step.Footer>
      <div className={stepFooterStyle}>
        <EditPrompt
          videoId={video.data?.id}
          placeholder={placeholder}
          completions={
            selectedCampaign?.[promptHintsKey] ?? selectedCampaign?.promptHints
          }
          getPromptTemplate={customerBranding.getPromptTemplate}
          onSubmit={handleSubmit}
          onReset={customerBranding.reset}
          isPending={customerBranding.isPending}
          onRedo={() =>
            handleRegenerateScript({
              onSuccess: (videoData) =>
                handleSubmit(undefined, undefined, videoData),
            })
          }
          styles={stepFooterPromptStyles}
          actions={
            hasHistoricalCampaigns && (
              <>
                <TooltipTrigger>
                  <ActionButton
                    onPress={handleUndo}
                    isDisabled={updateVideo.isPending || !hasPreviousCampaign}
                    aria-label="Undo"
                  >
                    <UndoIcon />
                  </ActionButton>
                  <Tooltip>Undo last changes</Tooltip>
                </TooltipTrigger>
                <TooltipTrigger>
                  <ActionButton
                    onPress={handleRedo}
                    isDisabled={updateVideo.isPending || !hasNextCampaign}
                    aria-label="Redo"
                  >
                    <RedoIcon />
                  </ActionButton>
                  <Tooltip>Revert changes</Tooltip>
                </TooltipTrigger>
              </>
            )
          }
        />
      </div>
    </Step.Footer>
  );
};

CampaignPromptFooter.propTypes = {
  editing: PropTypes.shape({
    hasHistoricalCampaigns: PropTypes.bool,
    hasPreviousCampaign: PropTypes.bool,
    hasNextCampaign: PropTypes.bool,
    handleSubmit: PropTypes.func.isRequired,
    handleUndo: PropTypes.func.isRequired,
    handleRedo: PropTypes.func.isRequired,
  }).isRequired,
  placeholder: PropTypes.string,
  promptHintsKey: PropTypes.string,
};
